// WebAudio singleton. Lazy-init on first user interaction so iOS Safari
// doesn't refuse the AudioContext. Persistence: mute + master volume in
// localStorage so the player's preference survives page reloads.
//
// All sounds are synthesized in pure WebAudio (no external assets) so the
// bundle stays small and we never have to ship binary blobs. SFX are
// procedural — defined in registry.ts as short functions over the AudioContext.
const STORAGE_KEY_MUTED = 'plunder.audio.muted';
const STORAGE_KEY_VOLUME = 'plunder.audio.volume';
class AudioMixer {
    ctx = null;
    master = null;
    muted = readBool(STORAGE_KEY_MUTED, false);
    volume = readFloat(STORAGE_KEY_VOLUME, 0.6);
    /** Lazy-init. Must be called from a user gesture handler the first time
     * to satisfy iOS Safari's audio autoplay policy. */
    ensure() {
        if (this.ctx)
            return this.ctx;
        const Ctx = (window.AudioContext || window.webkitAudioContext);
        if (!Ctx)
            return null;
        this.ctx = new Ctx();
        this.master = this.ctx.createGain();
        this.master.gain.value = this.muted ? 0 : this.volume;
        this.master.connect(this.ctx.destination);
        return this.ctx;
    }
    out() {
        this.ensure();
        return this.master;
    }
    setMuted(m) {
        this.muted = m;
        localStorage.setItem(STORAGE_KEY_MUTED, m ? '1' : '0');
        if (this.master)
            this.master.gain.value = m ? 0 : this.volume;
    }
    setVolume(v) {
        v = Math.max(0, Math.min(1, v));
        this.volume = v;
        localStorage.setItem(STORAGE_KEY_VOLUME, v.toFixed(3));
        if (this.master && !this.muted)
            this.master.gain.value = v;
    }
    /** Try to resume the AudioContext if suspended (iOS gesture unlock). */
    unlock() {
        const ctx = this.ensure();
        if (ctx && ctx.state === 'suspended')
            ctx.resume().catch(() => undefined);
    }
}
export const mixer = new AudioMixer();
function readBool(key, def) {
    try {
        const v = localStorage.getItem(key);
        if (v === null)
            return def;
        return v === '1' || v === 'true';
    }
    catch {
        return def;
    }
}
function readFloat(key, def) {
    try {
        const v = localStorage.getItem(key);
        if (v === null)
            return def;
        const n = parseFloat(v);
        return Number.isFinite(n) ? n : def;
    }
    catch {
        return def;
    }
}
